<template>
  <div>
    <div v-if="model === false">
      <div class="totalmakeHole" ref="total">
        <div
          class="topback"
          :style="{ height: '6vh', backgroundColor: scrollback ? 'rgb(245 244 246)' : 'rgb(245 244 246)' }"
        >
          <headtip :changeVisable="changeVisable" @showDialog="showPartner" />
        </div>

        <div class="makeHole1" @click="changeheadVisable">
          <div class="ip-page ip-page--desktop">
            <section class="hero" id="intelligent-injection-production">
              <div class="hero-inner">
                <div class="hero-content">
                  <h1>Intelligent Injection-Production Closed-Loop Optimization</h1>
                  <ul class="hero-list">
                    <li>Automatically identifies injection-production imbalance, ineffective water circulation, and differences in interlayer reservoir utilization.</li>
                    <li>Predicts the impact of water injection adjustment on production, water cut, and pressure.</li>
                    <li>Generates optimization recommendations for water injection volume, valve position settings, layer/interval allocation ratio, and adjustment timing.</li>
                  </ul>
                  <div class="hero-actions">
                    <a class="btn" href="javascript:void(0)" @click.prevent="scrollToConsult">Product Consultation</a>
                    <a class="btn" href="javascript:void(0)" @click.prevent="scrollToExperience">Feature Trial</a>
                  </div>
                </div>

                <div class="reservoir-visual" aria-label="Intelligent Injection-Production Closed-Loop Optimization Diagram">
                  <div class="ground"></div>
                  <div class="pump pump-a"></div>
                  <div class="pump pump-b"></div>
                  <div class="pump pump-c"></div>
                  <div class="layer layer-1"></div>
                  <div class="layer layer-2"></div>
                  <div class="layer layer-3"></div>
                  <div class="well-main"></div>
                  <div class="well-sub"></div>
                  <div class="flow-line flow-1"></div>
                  <div class="flow-line flow-2"></div>
                  <div class="flow-line flow-3"></div>
                  <div class="visual-tag tag-1">Injection Side</div>
                  <div class="visual-tag tag-2">Production Side</div>
                  <div class="visual-tag tag-3">Well Pattern Relationship</div>
                  <div class="visual-tag tag-4">Closed-loop Feedback</div>
                </div>
              </div>
            </section>

            <section class="section section-blue">
              <div class="container">
                <div class="section-head">
                  <h2 class="section-title">Feature Overview</h2>
                  <p class="section-desc">Well-Link Intelligent Injection-Production targets injection-production balance challenges in high-water-cut oilfield development. It connects injection wells, production wells, intervals, and well-pattern relationships, upgrading adjustment work from experience-based judgment to model-assisted decision-making.</p>
                </div>

                <div class="intro-layout">
                  <div class="intro-card">
                    <h3>Not Just Another Report, but a Closed-Loop Capability</h3>
                    <p>The system covers four steps—understanding field conditions, calculating responses, generating plans, and tracking execution—to help engineers quickly identify problems, create adjustment plans, and review optimization results.</p>
                  </div>

                  <div class="intro-steps">
                    <div class="step-card">
                      <div class="step-no">01</div>
                      <h3>Understand Field Conditions</h3>
                      <p>Aggregate dynamic data including injection, production, pressure, water cut, and valve position.</p>
                    </div>
                    <div class="step-card">
                      <div class="step-no">02</div>
                      <h3>Predict Response</h3>
                      <p>Evaluate post-adjustment trends in production, water cut, and pressure.</p>
                    </div>
                    <div class="step-card">
                      <div class="step-no">03</div>
                      <h3>Generate Plans</h3>
                      <p>Output recommendations for injection volume, interval allocation, and valve settings.</p>
                    </div>
                    <div class="step-card">
                      <div class="step-no">04</div>
                      <h3>Closed-Loop Review</h3>
                      <p>Track execution results and support rolling optimization.</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section class="section section-white" id="experience" ref="experience">
              <div class="container">
                <div class="section-head">
                  <h2 class="section-title">Core Functions</h2>
                </div>

                <div class="experience-grid">
                  <article class="experience-card">
                    <div class="icon-box">01</div>
                    <h3>Injection-Production Parameter Prediction</h3>
                    <p>Integrate production dynamics, intervals, porosity-permeability, and wellbore information to predict the impact of different adjustments on well-group behavior.</p>
                    <span class="solve">Determines: Will the adjustment be effective?</span>
                  </article>

                  <article class="experience-card">
                    <div class="icon-box">02</div>
                    <h3>Injection-Production Parameter Optimization</h3>
                    <p>Optimize across production, water cut, pressure, and equipment constraints to generate executable recommendations.</p>
                    <span class="solve">Answers: How can we adjust more effectively and steadily?</span>
                  </article>

                  <article class="experience-card">
                    <div class="icon-box">03</div>
                    <h3>Execution Feedback & Review</h3>
                    <p>Connect with field control systems to track plan dispatch, execution feedback, and performance changes, forming continuous optimization.</p>
                    <span class="solve">Confirms: Was the plan executed, and how did it perform?</span>
                  </article>
                </div>
              </div>
            </section>

            <section class="section section-soft">
              <div class="container">
                <div class="section-head">
                  <h2 class="section-title">Closed-Loop Injection-Production Management Process</h2>
                  <p class="section-desc">From issue identification to field execution, build a clear, traceable, and reviewable workflow.</p>
                </div>

                <div class="flow-panel">
                  <div class="flow-copy">
                    <h3>Data + Models + Optimization + Execution</h3>
                    <p>Move injection-production management beyond offline analysis and manual circulation into a field closed loop for continuous optimization.</p>
                  </div>

                  <div class="flow-steps">
                    <div class="flow-step">
                      <strong>STEP 01</strong>
                      <h3>Data Sensing</h3>
                      <p>Identify injection-production imbalance, abnormal fluctuations, and low-efficiency cycling.</p>
                    </div>
                    <div class="flow-step">
                      <strong>STEP 02</strong>
                      <h3>Model Prediction</h3>
                      <p>Predict dynamic responses after injection-production adjustments.</p>
                    </div>
                    <div class="flow-step">
                      <strong>STEP 03</strong>
                      <h3>Plan Optimization</h3>
                      <p>Generate recommendations for injection volume, valve position, and intervals.</p>
                    </div>
                    <div class="flow-step">
                      <strong>STEP 04</strong>
                      <h3>Execution Review</h3>
                      <p>Track execution results and perform rolling optimization.</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section class="section section-dark" id="consult" ref="consult">
              <div class="container">
                <div class="section-head">
                  <h2 class="section-title section-title--light">Application Value</h2>
                  <p class="section-desc section-desc--light">The goal is to make injection-production management more accurate, stable, efficient, and transparent.</p>
                </div>

                <div class="value-grid">
                  <div class="value-card">
                    <strong>More Accurate Injection</strong>
                    <span>Reduce ineffective cycling and improve the effective injection ratio.</span>
                  </div>
                  <div class="value-card">
                    <strong>More Stable Production</strong>
                    <span>Balance injection-production relationships and reduce fluctuation risks.</span>
                  </div>
                  <div class="value-card">
                    <strong>Faster Adjustment</strong>
                    <span>Shorten manual analysis and plan circulation cycles.</span>
                  </div>
                  <div class="value-card">
                    <strong>Clearer Management</strong>
                    <span>Make execution, feedback, and review fully traceable.</span>
                  </div>
                </div>

                <div class="cta">
                  <div>
                    <h2>Bring Injection-Production Management into an Intelligent Closed Loop</h2>
                    <p>Connect downhole operations, surface systems, and development decisions so data supports judgment, models serve production, and plans move into field execution.</p>
                  </div>
                  <a class="btn btn--cta" href="javascript:void(0)" @click.prevent="clickConsult('Oil & Gas Generic AI')">Book a Consultation</a>
                </div>
              </div>
            </section>
          </div>

          <bottomTip />
        </div>
      </div>
    </div>

    <div v-else>
      <div class="totalModel" ref="totalModel">
        <div class="screen1">
          <kefuMobile @topMobile="topTopmobile" />
          <headtip @showDialog="showPartner" />
          <div class="layer2">
            <div class="title1">Intelligent Injection-Production Closed-Loop Optimization</div>
            <div class="title2">
              <li>Automatically identify injection-production imbalance and ineffective water cycling</li>
              <li>Predict the impact of water-injection adjustment on production, water cut, and pressure</li>
              <li>Generate optimization recommendations for injection volume, valve position, and interval allocation</li>
            </div>
          </div>
          <div class="hero-actions-mobile">
            <div class="titlle3" @click="clickConsult('Oil & Gas Generic AI')">Product Consultation</div>
            <div class="titlle3 titlle3--outline" @click="scrollToExperience">Feature Experience</div>
          </div>
        </div>

        <div class="screenmake0">
          <div class="reservoir-visual reservoir-visual--mobile" aria-label="Intelligent Injection-Production Closed-Loop Optimization Diagram">
            <div class="ground"></div>
            <div class="pump pump-a"></div>
            <div class="pump pump-b"></div>
            <div class="pump pump-c"></div>
            <div class="layer layer-1"></div>
            <div class="layer layer-2"></div>
            <div class="layer layer-3"></div>
            <div class="well-main"></div>
            <div class="well-sub"></div>
            <div class="flow-line flow-1"></div>
            <div class="flow-line flow-2"></div>
            <div class="flow-line flow-3"></div>
            <div class="visual-tag tag-1">Injection Side</div>
            <div class="visual-tag tag-2">Production Side</div>
            <div class="visual-tag tag-3">Well Pattern Relationship</div>
            <div class="visual-tag tag-4">Closed-loop Feedback</div>
          </div>
        </div>

        <div class="screenmake2">
          <div class="heighLight">Feature Overview</div>
          <div class="heightLight2">
            Well-Link Intelligent Injection-Production targets injection-production balance challenges in high-water-cut oilfield development. It connects injection wells, production wells, intervals, and well-pattern relationships, upgrading adjustment work from experience-based judgment to model-assisted decision-making.
          </div>
          <div class="grid">
            <div class="card">
              <div class="back back--dark">
                <div class="titleDesign">
                  <div class="Title1">Not Just Another Report, but a Closed-Loop Capability</div>
                  <div class="Title2">
                    The system covers four steps—understanding field conditions, calculating responses, generating plans, and tracking execution—to help engineers quickly identify problems, create adjustment plans, and review optimization results.
                  </div>
                </div>
              </div>
            </div>
            <div class="card" v-for="item in introSteps" :key="item.no">
              <div class="back">
                <div class="titleDesign">
                  <div class="Title1"><span class="step-badge">{{ item.no }}</span>{{ item.title }}</div>
                  <div class="Title2">{{ item.desc }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="screenmake2" ref="experience">
          <div class="heighLight">Core Functions</div>
          <div class="grid">
            <div class="card" v-for="item in coreFeatures" :key="item.no">
              <div class="back">
                <div class="titleDesign">
                  <div class="Title1"><span class="step-badge">{{ item.no }}</span>{{ item.title }}</div>
                  <div class="Title2">{{ item.desc }}</div>
                  <div class="solve-tag">{{ item.solve }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="screenmake2 screenmake2--soft">
          <div class="heighLight">Closed-Loop Injection-Production Management Process</div>
          <div class="heightLight2">From issue identification to field execution, build a clear, traceable, and reviewable workflow.</div>
          <div class="grid">
            <div class="card">
              <div class="back back--dark">
                <div class="titleDesign">
                  <div class="Title1">Data + Models + Optimization + Execution</div>
                  <div class="Title2">Move injection-production management beyond offline analysis and manual circulation into a field closed loop for continuous optimization.</div>
                </div>
              </div>
            </div>
            <div class="card" v-for="item in flowSteps" :key="item.no">
              <div class="back">
                <div class="titleDesign">
                  <div class="Title1">{{ item.no }} {{ item.title }}</div>
                  <div class="Title2">{{ item.desc }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="screenmake2 screenmake2--dark" ref="consult">
          <div class="heighLight heighLight--light">Application Value</div>
          <div class="heightLight2 heightLight2--light">The goal is to make injection-production management more accurate, stable, efficient, and transparent.</div>
          <div class="grid">
            <div class="card" v-for="item in valueItems" :key="item.title">
              <div class="back back--value">
                <div class="titleDesign">
                  <div class="Title1">{{ item.title }}</div>
                  <div class="Title2">{{ item.desc }}</div>
                </div>
              </div>
            </div>
            <div class="card">
              <div class="back back--cta">
                <div class="titleDesign">
                  <div class="Title1">Bring Injection-Production Management into an Intelligent Closed Loop</div>
                  <div class="Title2">Connect downhole operations, surface systems, and development decisions so data supports judgment, models serve production, and plans move into field execution.</div>
                  <div class="titlle3 titlle3--cta" @click="clickConsult('Oil & Gas Generic AI')">Book a Consultation</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <bottomTip />
      </div>
    </div>

    <partnerTip :dialogVisible="dialogconsult" @hideDialog="hide" />
    <consultTip
      :dialogVisible="dialogVisible"
      :thisTitle="title"
      @hideDialog="hideDialog"
    />
  </div>
</template>

<script>
import headtip from '@/views/headTip.vue'
import bottomTip from '@/views/bottomTip.vue'
import consultTip from '@/components/consultTip.vue'
import PartnerTip from '@/components/partnerTip.vue'
import kefuMobile from '@/views/kefuMobile.vue'
import { shareUrl } from '@/utils/vxshare'

export default {
  name: 'injectionProduction',
  components: {
    PartnerTip,
    consultTip,
    headtip,
    bottomTip,
    kefuMobile
  },
  data() {
    return {
      changeVisable: 1,
      title: '',
      model: false,
      dialogconsult: false,
      scrollback: false,
      dialogVisible: false,
      pageTitle: 'Intelligent Injection-Production Closed-Loop Optimization - Oil & Gas Generic AI',
      pageDescription: 'Intelligent Injection-Production targets injection-production balance challenges in high-water-cut oilfield development. It connects injection wells, production wells, intervals, and well-pattern relationships, upgrading adjustment work from experience-based judgment to model-assisted decision-making.',
      pageKeywords: 'Intelligent Injection-Production Closed-Loop Optimization, Injection-Production Parameter Prediction, Injection-Production Parameter Optimization, Oil & Gas Generic AI',
      introSteps: [
        { no: '01', title: 'Understand Field Conditions', desc: 'Aggregate dynamic data including injection, production, pressure, water cut, and valve position.' },
        { no: '02', title: 'Predict Response', desc: 'Evaluate post-adjustment trends in production, water cut, and pressure.' },
        { no: '03', title: 'Generate Plans', desc: 'Output recommendations for injection volume, interval allocation, and valve settings.' },
        { no: '04', title: 'Closed-Loop Review', desc: 'Track execution results and support rolling optimization.' }
      ],
      coreFeatures: [
        { no: '01', title: 'Injection-Production Parameter Prediction', desc: 'Integrate production dynamics, intervals, porosity-permeability, and wellbore information to predict the impact of different adjustments on well-group behavior.', solve: 'Determines: Will the adjustment be effective?' },
        { no: '02', title: 'Injection-Production Parameter Optimization', desc: 'Optimize across production, water cut, pressure, and equipment constraints to generate executable recommendations.', solve: 'Answers: How can we adjust more effectively and steadily?' },
        { no: '03', title: 'Execution Feedback & Review', desc: 'Connect with field control systems to track plan dispatch, execution feedback, and performance changes, forming continuous optimization.', solve: 'Confirms: Was the plan executed, and how did it perform?' }
      ],
      flowSteps: [
        { no: 'STEP 01', title: 'Data Sensing', desc: 'Identify injection-production imbalance, abnormal fluctuations, and low-efficiency cycling.' },
        { no: 'STEP 02', title: 'Model Prediction', desc: 'Predict dynamic responses after injection-production adjustments.' },
        { no: 'STEP 03', title: 'Plan Optimization', desc: 'Generate recommendations for injection volume, valve position, and intervals.' },
        { no: 'STEP 04', title: 'Execution Review', desc: 'Track execution results and perform rolling optimization.' }
      ],
      valueItems: [
        { title: 'More Accurate Injection', desc: 'Reduce ineffective cycling and improve the effective injection ratio.' },
        { title: 'More Stable Production', desc: 'Balance injection-production relationships and reduce fluctuation risks.' },
        { title: 'Faster Adjustment', desc: 'Shorten manual analysis and plan circulation cycles.' },
        { title: 'Clearer Management', desc: 'Make execution, feedback, and review fully traceable.' }
      ]
    }
  },
  methods: {
    topTopmobile() {
      const ref = this.model ? this.$refs.totalModel : this.$refs.total
      if (ref) {
        ref.scrollTop = 0
      }
    },
    topTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    },
    changeheadVisable() {
      this.changeVisable = this.changeVisable === 1 ? 0 : 1
    },
    clickConsult(item) {
      this.title = item
      this.dialogVisible = true
      sessionStorage.setItem('title', item)
    },
    hide() {
      this.dialogconsult = false
    },
    hideDialog() {
      this.dialogVisible = false
    },
    showPartner() {
      this.dialogconsult = true
    },
    scrollToSection(refName) {
      const section = this.$refs[refName]
      if (!section) return
      section.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    scrollToExperience() {
      this.scrollToSection('experience')
    },
    scrollToConsult() {
      this.scrollToSection('consult')
    },
    handleScroll() {
      const ref = this.model ? this.$refs.totalModel : this.$refs.total
      if (!ref) return
      this.scrollback = ref.scrollTop >= 350
    },
    setMetaTags() {
      const metaDescription = document.querySelector('meta[name="description"]')
      if (metaDescription) {
        metaDescription.setAttribute('content', this.pageDescription)
      }

      const metaKeywords = document.querySelector('meta[name="keywords"]')
      if (metaKeywords) {
        metaKeywords.setAttribute('content', this.pageKeywords)
      }
    }
  },
  mounted() {
    const screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
    this.model = screenWidth <= 700

    this.$nextTick(() => {
      const ref = this.model ? this.$refs.totalModel : this.$refs.total
      if (ref) {
        ref.addEventListener('scroll', this.handleScroll)
      }
    })

    document.title = this.pageTitle
    this.setMetaTags()
    localStorage.setItem('home', 0)

    const url = window.location.href
    setTimeout(() => {
      shareUrl({
        url,
        pageTitle: this.pageTitle,
        pageDescription: this.pageDescription,
        pageKeywords: this.pageKeywords
      })
    }, 1000)

    document.dispatchEvent(new Event('render-event'))
  },
  beforeDestroy() {
    const ref = this.model ? this.$refs.totalModel : this.$refs.total
    if (ref) {
      ref.removeEventListener('scroll', this.handleScroll)
    }
  }
}
</script>

<style lang="scss" scoped>
.totalmakeHole {
  background: #fff;
  overflow-x: hidden;
  width: 100vw;
  height: 100vh;
  overflow-y: auto;
  position: relative;

  .topback {
    position: sticky;
    top: 0;
    z-index: 999;
    width: 100%;
    padding: 0 0 0 2.86077411900635vw;
    box-sizing: border-box;
  }
}

.totalModel {
  background: #ebf1fd;
  overflow-x: hidden;
  width: 100vw;
  position: relative;

  .screen1 {
    position: relative;
    width: 100%;
    padding: 12px 15px 20px;
    background: linear-gradient(90deg, #dfeeff 0%, #eaf4ff 42%, #d5e6f5 100%);

    .layer2 {
      margin-top: 50px;

      .title1 {
        margin: 0 auto;
        width: 92%;
        text-align: center;
        font-size: 20px;
        font-family: Sarial, sans-serif, "Microsoft Yahei";
        font-weight: 700;
        color: #252f87;
        line-height: 26px;
      }

      .title2 {
        margin: 16px auto 0;
        width: 92%;
        text-align: left;
        font-size: 14px;
        font-family: Sarial, sans-serif, "Microsoft Yahei";
        font-weight: 400;
        color: #252f87;
        line-height: 22px;
        list-style: none;
        padding: 0;

        li {
          position: relative;
          margin-bottom: 8px;
          padding-left: 14px;

          &::before {
            content: "";
            position: absolute;
            left: 0;
            top: 9px;
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: #252f87;
          }
        }
      }
    }

    .hero-actions-mobile {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: stretch;
      gap: 10px;
      margin: 20px auto 8px;
      width: 92%;
    }

    .titlle3 {
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
      box-sizing: border-box;
      flex: 1;
      min-width: 0;
      min-height: 44px;
      font-size: 13px;
      font-family: Sarial, sans-serif, "Microsoft Yahei";
      font-weight: 700;
      color: #fff;
      border-radius: 8px;
      padding: 10px 8px;
      background: #2f72ff;
      border: 1px solid #2f72ff;
      line-height: 1.25;
      text-align: center;

      &--outline {
        background: #fff;
        color: #2f72ff;
        border: 1.5px solid #2f72ff;
      }

      &--cta {
        width: fit-content;
        flex: none;
        margin-top: 14px;
      }
    }
  }

  .screenmake0 {
    padding: 16px 15px;
    background: #fff;
  }

  .screenmake2 {
    padding: 20px 15px;
    width: 100%;
    background: #fdfdfd;

    &--soft {
      background: #f0f7ff;
    }

    &--dark {
      background: linear-gradient(120deg, #0a3357 0%, #0b4b73 56%, #103b68 100%);
    }

    .heighLight {
      text-align: center;
      font-size: 21px;
      font-family: Sarial, sans-serif, "Microsoft Yahei";
      font-weight: 700;
      line-height: 26px;
      color: #111;

      &--light {
        color: #fff;
      }
    }

    .heightLight2 {
      margin: 14px 0 0;
      text-align: justify;
      font-size: 14px;
      font-family: Sarial, sans-serif, "Microsoft Yahei";
      font-weight: 400;
      color: #657386;
      line-height: 22px;

      &--light {
        color: rgba(255, 255, 255, 0.78);
        text-align: center;
      }
    }

    .grid {
      margin-top: 16px;
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      row-gap: 12px;
    }

    .card {
      width: 100%;

      .back {
        border-radius: 12px;
        padding: 16px;
        background: #fff;
        border: 1px solid #e5ecf5;
        box-shadow: 0 6px 18px rgba(16, 43, 92, 0.06);

        .titleDesign {
          .Title1 {
            font-size: 16px;
            font-weight: 700;
            color: #142033;
            line-height: 24px;
            margin-bottom: 8px;
          }

          .Title2 {
            font-size: 14px;
            font-weight: 400;
            color: #657386;
            line-height: 22px;
          }
        }

        &--dark {
          background: linear-gradient(160deg, #0f4f7c 0%, #0a3357 100%);
          border: none;

          .titleDesign {
            .Title1 {
              color: #fff;
            }

            .Title2 {
              color: rgba(255, 255, 255, 0.9);
            }
          }
        }

        &--value {
          background: rgba(255, 255, 255, 0.12);
          border: 1px solid rgba(255, 255, 255, 0.28);

          .titleDesign {
            .Title1 {
              color: #fff;
              font-size: 20px;
              text-align: center;
            }

            .Title2 {
              color: rgba(255, 255, 255, 0.88);
              text-align: center;
            }
          }
        }

        &--cta {
          background: rgba(255, 255, 255, 0.12);
          border: 1px solid rgba(255, 255, 255, 0.2);

          .titleDesign {
            .Title1 {
              color: #fff;
            }

            .Title2 {
              color: rgba(255, 255, 255, 0.88);
            }

            .titlle3--cta {
              cursor: pointer;
              display: inline-flex;
              justify-content: center;
              align-items: center;
              width: fit-content;
              min-width: 160px;
              margin-top: 18px;
              padding: 12px 28px;
              border-radius: 8px;
              background: #fff;
              color: #0d4f7a;
              font-size: 16px;
              font-family: Sarial, sans-serif, "Microsoft Yahei";
              font-weight: 700;
              line-height: 1.2;
              box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18);
              border: none;
            }
          }
        }
      }

      .step-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        margin-right: 8px;
        border-radius: 8px;
        background: #eaf3ff;
        color: #0d4f7a;
        font-size: 12px;
        font-weight: 700;
        vertical-align: middle;
      }

      .solve-tag {
        display: inline-flex;
        margin-top: 12px;
        padding: 6px 10px;
        border-radius: 999px;
        background: #fff5e6;
        color: #a55a00;
        font-size: 12px;
        font-weight: 700;
      }
    }
  }

  .reservoir-visual--mobile {
    position: relative;
    height: 220px;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 12px 28px rgba(22, 52, 86, 0.18);
    background: linear-gradient(180deg, rgba(213, 222, 218, 0.88) 0%, rgba(186, 147, 101, 0.78) 30%, rgba(72, 64, 58, 0.92) 62%, rgba(36, 39, 46, 0.98) 100%);

    &::before {
      content: "";
      position: absolute;
      inset: 0;
      background:
        linear-gradient(175deg, transparent 0 19%, rgba(110, 86, 54, 0.35) 20% 23%, transparent 24% 100%),
        repeating-linear-gradient(172deg, rgba(26, 76, 118, 0.5) 0 2px, transparent 2px 16px);
      opacity: 0.65;
    }

    .ground {
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      height: 32%;
      background:
        radial-gradient(circle at 18% 45%, rgba(13, 79, 122, 0.22), transparent 18%),
        radial-gradient(circle at 72% 30%, rgba(255, 159, 26, 0.15), transparent 18%),
        linear-gradient(8deg, #9f8c6c 0%, #c8b898 50%, #dde6e7 100%);
      clip-path: polygon(0 0, 100% 0, 100% 78%, 0 100%);
    }

    .pump {
      position: absolute;
      width: 36px;
      height: 20px;
      top: 40px;
      border-radius: 4px;
      background: #0c3357;
      opacity: 0.9;
    }

    .pump-a { left: 40px; transform: rotate(-8deg); }
    .pump-b { left: 120px; top: 58px; transform: rotate(8deg); }
    .pump-c { right: 60px; top: 44px; transform: rotate(-5deg); }

    .well-main,
    .well-sub {
      position: absolute;
      top: 28px;
      width: 7px;
      border-radius: 999px;
    }

    .well-main {
      left: 60%;
      height: 170px;
      background: linear-gradient(180deg, #ffb454, #ff8b20);
    }

    .well-sub {
      left: 80%;
      top: 70px;
      height: 120px;
      background: linear-gradient(180deg, #56b4ff, #2f72ff);
    }

    .layer {
      position: absolute;
      left: 0;
      right: 0;
      height: 2px;
      background: rgba(255, 255, 255, 0.2);
    }

    .layer-1 { top: 48%; }
    .layer-2 { top: 62%; }
    .layer-3 { top: 76%; }

    .flow-line {
      position: absolute;
      height: 2px;
      border-radius: 999px;
      background: linear-gradient(90deg, rgba(59, 183, 216, 0), rgba(59, 183, 216, 0.88), rgba(255, 180, 84, 0.95));
    }

    .flow-1 { width: 52%; left: 20%; top: 55%; transform: rotate(-14deg); }
    .flow-2 { width: 45%; left: 28%; top: 64%; transform: rotate(-8deg); opacity: 0.85; }
    .flow-3 { width: 37%; left: 42%; top: 73%; transform: rotate(-2deg); opacity: 0.72; }

    .visual-tag {
      position: absolute;
      padding: 5px 8px;
      border-radius: 999px;
      color: #fff;
      font-size: 10px;
      font-weight: 700;
      background: rgba(11, 45, 76, 0.76);
      border: 1px solid rgba(255, 255, 255, 0.16);
    }

    .tag-1 { left: 12px; top: 12px; }
    .tag-2 { right: 12px; top: 12px; }
    .tag-3 { left: 14px; bottom: 14px; }
    .tag-4 { right: 14px; bottom: 14px; }
  }
}

.ip-page {
  --navy: #102b5c;
  --blue: #2f72ff;
  --deep-blue: #0d4f7a;
  --cyan: #3bb7d8;
  --text: #142033;
  --muted: #657386;
  --line: #e5ecf5;
  --shadow: 0 16px 42px rgba(16, 43, 92, 0.10);
  --soft-shadow: 0 10px 28px rgba(16, 43, 92, 0.07);

  color: var(--text);
  line-height: 1.7;
  font-family: "Microsoft YaHei", "PingFang SC", Arial, sans-serif;

  &--desktop {
    padding-top: 0;
  }

  a {
    color: inherit;
    text-decoration: none;
    cursor: pointer;
  }

  .container {
    width: min(1180px, calc(100% - 40px));
    margin: 0 auto;
  }

  .hero {
    min-height: auto;
    background: linear-gradient(90deg, #dfeeff 0%, #eaf4ff 42%, #d5e6f5 100%);
    overflow: hidden;
    position: relative;

    &::after {
      content: "";
      position: absolute;
      right: -120px;
      bottom: -150px;
      width: 520px;
      height: 520px;
      border-radius: 50%;
      background: rgba(47, 114, 255, 0.08);
    }
  }

  .hero-inner {
    width: min(1280px, calc(100% - 64px));
    margin: 0 auto;
    min-height: 0;
    padding: 40px 0;
    display: grid;
    grid-template-columns: 1.05fr 1.15fr;
    gap: 36px;
    align-items: center;
    position: relative;
    z-index: 1;
  }

  .hero-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0;
  }

  .hero h1 {
    margin: 0 0 18px;
    color: #252f87;
    font-size: 36px;
    line-height: 1.22;
    font-weight: 900;
    letter-spacing: -0.4px;
  }

  .hero-list {
    margin: 0 0 22px;
    padding: 0;
    list-style: none;
  }

  .hero-list li {
    position: relative;
    margin-bottom: 12px;
    padding-left: 24px;
    color: #252f87;
    font-size: 17px;
    line-height: 1.45;
    font-weight: 700;
  }

  .hero-list li:last-child {
    margin-bottom: 0;
  }

  .hero-list li::before {
    content: "";
    position: absolute;
    left: 0;
    top: 10px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #252f87;
  }

  .hero-actions {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
  }

  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 168px;
    height: 44px;
    padding: 0 20px;
    border-radius: 8px;
    background: var(--blue);
    color: #fff;
    font-size: 16px;
    font-weight: 800;
    box-shadow: 0 10px 24px rgba(47, 114, 255, 0.22);
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 14px 28px rgba(47, 114, 255, 0.28);
    }

    &--cta {
      background: #fff;
      color: var(--deep-blue);
      box-shadow: none;
      flex: 0 0 auto;
    }
  }

  .reservoir-visual {
    position: relative;
    height: 350px;
    border-radius: 22px;
    overflow: hidden;
    box-shadow: 0 24px 56px rgba(22, 52, 86, 0.24);
    background: linear-gradient(180deg, rgba(213, 222, 218, 0.88) 0%, rgba(186, 147, 101, 0.78) 30%, rgba(72, 64, 58, 0.92) 62%, rgba(36, 39, 46, 0.98) 100%);

    &::before {
      content: "";
      position: absolute;
      inset: 0;
      background:
        linear-gradient(175deg, transparent 0 19%, rgba(110, 86, 54, 0.35) 20% 23%, transparent 24% 100%),
        repeating-linear-gradient(172deg, rgba(26, 76, 118, 0.5) 0 2px, transparent 2px 16px);
      opacity: 0.65;
    }
  }

  .ground {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 32%;
    background:
      radial-gradient(circle at 18% 45%, rgba(13, 79, 122, 0.22), transparent 18%),
      radial-gradient(circle at 72% 30%, rgba(255, 159, 26, 0.15), transparent 18%),
      linear-gradient(8deg, #9f8c6c 0%, #c8b898 50%, #dde6e7 100%);
    clip-path: polygon(0 0, 100% 0, 100% 78%, 0 100%);
  }

  .pump {
    position: absolute;
    width: 46px;
    height: 26px;
    top: 52px;
    border-radius: 4px;
    background: #0c3357;
    opacity: 0.9;

    &::before {
      content: "";
      position: absolute;
      width: 54px;
      height: 3px;
      left: -4px;
      bottom: -7px;
      background: #0c3357;
    }
  }

  .pump-a { left: 64px; transform: rotate(-8deg); }
  .pump-b { left: 178px; top: 78px; transform: rotate(8deg); }
  .pump-c { right: 100px; top: 58px; transform: rotate(-5deg); }

  .well-main,
  .well-sub {
    position: absolute;
    top: 34px;
    width: 9px;
    border-radius: 999px;
    background: linear-gradient(180deg, #ffb454, #ff8b20);
    box-shadow: 0 0 18px rgba(255, 159, 26, 0.34);
  }

  .well-main { left: 60%; height: 292px; }
  .well-sub {
    left: 80%;
    top: 92px;
    height: 205px;
    background: linear-gradient(180deg, #56b4ff, #2f72ff);
    box-shadow: 0 0 18px rgba(47, 114, 255, 0.28);
  }

  .layer {
    position: absolute;
    left: 0;
    right: 0;
    height: 2px;
    background: rgba(255, 255, 255, 0.20);
    box-shadow: 0 0 10px rgba(47, 114, 255, 0.12);
  }

  .layer-1 { top: 48%; }
  .layer-2 { top: 62%; }
  .layer-3 { top: 76%; }

  .flow-line {
    position: absolute;
    height: 3px;
    border-radius: 999px;
    transform-origin: left center;
    background: linear-gradient(90deg, rgba(59, 183, 216, 0), rgba(59, 183, 216, 0.88), rgba(255, 180, 84, 0.95));
    box-shadow: 0 0 18px rgba(59, 183, 216, 0.32);
  }

  .flow-1 { width: 52%; left: 20%; top: 55%; transform: rotate(-14deg); }
  .flow-2 { width: 45%; left: 28%; top: 64%; transform: rotate(-8deg); opacity: 0.85; }
  .flow-3 { width: 37%; left: 42%; top: 73%; transform: rotate(-2deg); opacity: 0.72; }

  .visual-tag {
    position: absolute;
    padding: 8px 12px;
    border-radius: 999px;
    color: #fff;
    font-size: 11px;
    line-height: 1;
    font-weight: 700;
    background: rgba(11, 45, 76, 0.76);
    border: 1px solid rgba(255, 255, 255, 0.16);
    backdrop-filter: blur(8px);
  }

  .tag-1 { left: 18px; top: 18px; }
  .tag-2 { right: 18px; top: 18px; }
  .tag-3 { left: 22px; bottom: 20px; }
  .tag-4 { right: 22px; bottom: 20px; }

  .section {
    position: relative;
    padding: 74px 0;
  }

  .section-blue {
    background: linear-gradient(180deg, #f0f7ff 0%, #f7fbff 100%);
  }

  .section-white {
    background: #fff;
  }

  .section-soft {
    background:
      radial-gradient(circle at 20% 10%, rgba(47, 114, 255, 0.07), transparent 26%),
      linear-gradient(180deg, #f8fbff 0%, #eef6ff 100%);
  }

  .section-dark {
    padding: 72px 0 80px;
    background:
      radial-gradient(circle at 15% 0%, rgba(59, 183, 216, 0.20), transparent 28%),
      linear-gradient(120deg, #0a3357 0%, #0b4b73 56%, #103b68 100%);
    color: #fff;
  }

  .section-white::before,
  .section-soft::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: -1px;
    height: 34px;
    background: inherit;
    clip-path: polygon(0 0, 100% 0, 100% 58%, 0 100%);
    z-index: 0;
  }

  .section > .container {
    position: relative;
    z-index: 1;
  }

  .section-head {
    margin-bottom: 36px;
    text-align: center;
  }

  .section-title {
    margin: 0;
    font-size: 38px;
    line-height: 1.2;
    font-weight: 900;
    color: #142033;
    letter-spacing: -0.3px;

    &--light {
      color: #fff;
    }
  }

  .section-desc {
    max-width: 790px;
    margin: 16px auto 0;
    color: var(--muted);
    font-size: 17px;
    font-weight: 600;

    &--light {
      color: rgba(255, 255, 255, 0.72);
    }
  }

  .intro-layout {
    display: grid;
    grid-template-columns: 0.85fr 1.15fr;
    gap: 26px;
    align-items: stretch;
  }

  .intro-card {
    padding: 34px;
    border-radius: 26px;
    background: linear-gradient(160deg, #0f4f7c 0%, #0a3357 100%);
    color: #fff;
    box-shadow: var(--shadow);
    overflow: hidden;
    position: relative;

    &::after {
      content: "";
      position: absolute;
      right: -72px;
      bottom: -72px;
      width: 180px;
      height: 180px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.08);
    }

    h3 {
      margin: 0 0 18px;
      font-size: 28px;
      line-height: 1.25;
      font-weight: 900;
    }

    p {
      margin: 0;
      color: rgba(255, 255, 255, 0.82);
      font-size: 17px;
      font-weight: 600;
    }
  }

  .intro-steps {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }

  .step-card {
    padding: 24px;
    border-radius: 22px;
    background: rgba(255, 255, 255, 0.88);
    border: 1px solid rgba(221, 232, 245, 0.95);
    box-shadow: var(--soft-shadow);
  }

  .step-no {
    display: inline-flex;
    width: 36px;
    height: 36px;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
    border-radius: 12px;
    background: #eaf3ff;
    color: var(--deep-blue);
    font-size: 15px;
    font-weight: 900;
  }

  .step-card h3 {
    margin: 0 0 8px;
    font-size: 19px;
    font-weight: 900;
    color: #1b2f47;
  }

  .step-card p {
    margin: 0;
    color: var(--muted);
    font-size: 15px;
    font-weight: 600;
  }

  .experience-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 22px;
  }

  .experience-card {
    min-height: 278px;
    padding: 30px 28px;
    border-radius: 26px;
    background: #fff;
    border: 1px solid var(--line);
    box-shadow: var(--soft-shadow);
    position: relative;
    overflow: hidden;
    transition: transform 0.22s ease, box-shadow 0.22s ease;
    display: flex;
    flex-direction: column;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 18px 44px rgba(16, 43, 92, 0.12);
    }

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 4px;
      background: linear-gradient(90deg, var(--blue), var(--cyan));
    }

    h3 {
      margin: 0 0 12px;
      color: #17243a;
      font-size: 24px;
      font-weight: 900;
    }

    p {
      margin: 0;
      color: var(--muted);
      font-size: 16px;
      font-weight: 600;
      flex: 1;
    }
  }

  .icon-box {
    width: 48px;
    height: 48px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 18px;
    border-radius: 16px;
    background: #f0f7ff;
    color: var(--deep-blue);
    font-size: 18px;
    font-weight: 900;
    flex-shrink: 0;
  }

  .solve {
    display: inline-flex;
    align-self: flex-start;
    margin-top: 22px;
    padding: 8px 13px;
    border-radius: 999px;
    background: #fff5e6;
    color: #a55a00;
    font-size: 14px;
    font-weight: 800;
  }

  .flow-panel {
    display: grid;
    grid-template-columns: 280px 1fr;
    gap: 22px;
    align-items: stretch;
  }

  .flow-copy {
    padding: 32px;
    border-radius: 26px;
    color: #fff;
    background: linear-gradient(160deg, #0e5885 0%, #0a3357 100%);
    box-shadow: var(--shadow);

    h3 {
      margin: 0 0 14px;
      font-size: 28px;
      line-height: 1.25;
      font-weight: 900;
    }

    p {
      margin: 0;
      color: rgba(255, 255, 255, 0.82);
      font-size: 16px;
      font-weight: 600;
    }
  }

  .flow-steps {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
  }

  .flow-step {
    padding: 26px 20px;
    border-radius: 22px;
    background: #fff;
    border: 1px solid var(--line);
    box-shadow: var(--soft-shadow);
    position: relative;

    &::after {
      content: "";
      position: absolute;
      right: -13px;
      top: 50%;
      width: 24px;
      height: 2px;
      background: #bfd6ef;
    }

    &:last-child::after {
      display: none;
    }

    strong {
      display: block;
      margin-bottom: 10px;
      color: var(--deep-blue);
      font-size: 15px;
      font-weight: 900;
    }

    h3 {
      margin: 0 0 8px;
      color: #1b2f47;
      font-size: 18px;
      font-weight: 900;
    }

    p {
      margin: 0;
      color: var(--muted);
      font-size: 14px;
      font-weight: 600;
    }
  }

  .value-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 18px;
  }

  .value-card {
    padding: 30px 22px;
    border-radius: 26px;
    background: rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.22);
    text-align: center;
    box-shadow: 0 12px 34px rgba(0, 0, 0, 0.10);

    strong {
      display: block;
      margin-bottom: 10px;
      color: #fff;
      font-size: 28px;
      line-height: 1.2;
      font-weight: 900;
    }

    span {
      color: rgba(255, 255, 255, 0.78);
      font-size: 15px;
      font-weight: 650;
    }
  }

  .cta {
    margin-top: 34px;
    padding: 34px;
    border-radius: 26px;
    background: rgba(255, 255, 255, 0.10);
    border: 1px solid rgba(255, 255, 255, 0.14);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;

    h2 {
      margin: 0 0 8px;
      color: #fff;
      font-size: 32px;
      font-weight: 900;
    }

    p {
      margin: 0;
      color: rgba(255, 255, 255, 0.78);
      font-size: 16px;
      font-weight: 600;
    }
  }

  @media (max-width: 1120px) {
    .hero-inner,
    .intro-layout,
    .flow-panel {
      grid-template-columns: 1fr;
    }

    .hero-inner {
      padding: 28px 0 36px;
    }

    .hero h1 {
      font-size: 32px;
    }

    .hero-list li {
      font-size: 16px;
    }

    .reservoir-visual {
      height: 300px;
    }

    .experience-grid,
    .value-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .flow-steps {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .flow-step::after {
      display: none;
    }
  }
}
</style>
